import type { Encoding, Service } from "@/types/api";

/**
 * Provider traits that both Studios need to agree on. Kept here rather than in
 * either Studio so the two cannot drift: a provider the engine will not list
 * has to be exempt from resource validation in both, or one of them blocks a
 * pipeline the other happily builds.
 */

/**
 * Providers whose resources cannot be enumerated, and why they are exempt from
 * resource validation rather than merely tolerant of a failed lookup:
 *
 * - `generator` — the resource is a caller-chosen stream label attached to the
 *   emitted events, not a collection or topic that exists to be found.
 * - `http_source` — routes are declared on the service by the user.
 */
export const UNLISTABLE_RESOURCE_PROVIDERS = new Set<Service["provider"]>([
  "generator",
  "http_source",
]);

export function hasUserDefinedResources(provider: string): boolean {
  return UNLISTABLE_RESOURCE_PROVIDERS.has(provider as Service["provider"]);
}

/**
 * Providers that can only ever be a pipeline's source. A generator produces a
 * stream and has no writer; an HTTP source is an inbound endpoint; a scanner
 * and a change capture read a target they never write back to.
 *
 * The engine refuses all three as a sink, but only when the pipeline is built
 * — so leaving one out here means a stage that can be dropped and then fails
 * on run.
 */
export const SOURCE_ONLY_PROVIDERS = new Set<Service["provider"]>([
  "generator",
  "http_source",
  "scanner",
  "postgres_cdc",
]);

/**
 * Providers that can only ever be a pipeline's sink. A PostgreSQL service is
 * read through a scanner or a change capture (`postgres_cdc`) that targets
 * it, never directly.
 */
export const SINK_ONLY_PROVIDERS = new Set<Service["provider"]>(["postgres"]);

/**
 * Providers a scanner can target: a scan pages through an ordered, unique
 * key, which a collection's `_id` and a table's primary key provide and a
 * topic does not.
 */
export const SCANNABLE_PROVIDERS = new Set<Service["provider"]>([
  "mongodb",
  "postgres",
]);

/** The services a scanner may target, in their listed order. */
export function scannableTargets(services: readonly Service[]): Service[] {
  return services.filter((service) =>
    SCANNABLE_PROVIDERS.has(service.provider),
  );
}

/**
 * Providers whose source stage cannot run without a schema. The generator
 * builds its documents from one, so without it there is nothing to produce —
 * the engine refuses to start such a pipeline, and the UI should say so before
 * the user gets that far.
 */
export const SCHEMA_REQUIRED_SOURCE_PROVIDERS = new Set<Service["provider"]>([
  "generator",
]);

/**
 * What a source stage's `input_encoding` means per provider, mirroring the
 * engine's `resolve_source_encoding` (mstream src/provision/pipeline/source.rs,
 * re-checked through v0.183.0): Kafka and Pub/Sub have no default and fail
 * to start without one; an HTTP source defaults to json; MongoDB, Scanner,
 * Generator and change capture decide the encoding themselves. One table so
 * the generator's default, the lint and the "Source delivers" control cannot
 * drift apart.
 */
export type SourceEncodingRule =
  | { kind: "required" }
  | { kind: "optional"; fallback: Encoding }
  | { kind: "fixed"; value: Encoding }
  | { kind: "none" };

export function sourceEncodingRule(
  provider: string | undefined,
): SourceEncodingRule {
  switch (provider as Service["provider"] | undefined) {
    case "kafka":
    case "pubsub":
      return { kind: "required" };
    case "http_source":
      return { kind: "optional", fallback: "json" };
    case "mongodb":
    case "scanner":
      return { kind: "fixed", value: "bson" };
    case "generator":
    case "postgres_cdc":
      return { kind: "fixed", value: "json" };
    default:
      return { kind: "none" };
  }
}

/** Whether the source's input encoding is the user's to pick (Kafka,
 *  Pub/Sub, HTTP source) rather than fixed by the provider. */
export function sourceEncodingIsChosen(provider: string | undefined): boolean {
  const kind = sourceEncodingRule(provider).kind;
  return kind === "required" || kind === "optional";
}

/**
 * Providers that back the AI assistant rather than a pipeline. They are hidden
 * from both Studios' explorers: nothing can be staged from them, so listing
 * them only invites a drag that cannot land. Mirrors the engine's
 * `Service::is_llm`.
 */
export const LLM_PROVIDERS = new Set<Service["provider"]>([
  "anthropic",
  "openai",
  "bedrock",
]);

export function isLlmProvider(provider: string): boolean {
  return LLM_PROVIDERS.has(provider as Service["provider"]);
}

/**
 * The stream label offered for a generator, which has no resources to browse.
 * Any value works — it is echoed to sinks as the `resource` attribute — so
 * this is a starting point the user can rename, not a lookup.
 */
export const GENERATOR_STREAM_RESOURCE = "stream";

/**
 * User-facing provider names — the explorer's section titles and the
 * linter's wording for a service used in the wrong role.
 */
export const PROVIDER_LABELS: Record<Service["provider"], string> = {
  mongodb: "MongoDB",
  postgres: "PostgreSQL",
  postgres_cdc: "PostgreSQL CDC",
  kafka: "Kafka",
  pubsub: "Google Pub/Sub",
  http: "HTTP Client",
  http_source: "HTTP Source",
  generator: "Generator",
  scanner: "Scanner",
  udf: "Scripts (UDF)",
  bedrock: "AWS Bedrock",
  anthropic: "Anthropic",
  openai: "OpenAI",
};
